import { useEffect, useRef, useState } from 'react';
import { CheckIcon, RefreshCwIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { leesbareFout, type Verwerkingsfase } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '../../lib/apiClient';
import { tijdTekst } from '../gesprekken/opname';
import {
  useGesprek,
  useBewerkTranscript,
  useHersamenvat,
  useKeurTranscriptGoed,
  useTranscript,
  useVoortgang,
} from '../gesprekken/gesprekken.api';
import { useGeleidelijk } from './geleidelijk';
import { useVerstrekenTijd } from './verstreken';
import { Verwerkingsspoor } from './Verwerkingsspoor';

/** Zo dicht bij de onderkant mag je nog "hij leest mee" heten. */
const BIJ_HET_EIND = 64;

/**
 * De laatste tussenstand die er werkelijk was.
 *
 * `null` betekent opgeruimd en niet leeg. De server wist zijn tussenstand zodra
 * hij klaar is, en dat kan een tik vóór het antwoord van de mutatie aankomen;
 * zonder dit onderscheid stond het tekstvak dan even leeg voordat de nieuwe
 * samenvatting erin viel. Een lege `tekst` mét stand is wél echt leeg: zo begint
 * de samenvatfase, met een schoon vlak.
 */
function useLaatsteStand(tekst: string | null, actief: boolean): string {
  const vastgehouden = useRef('');
  if (!actief) vastgehouden.current = '';
  else if (tekst !== null) vastgehouden.current = tekst;
  return vastgehouden.current;
}

/**
 * Meeschuiven met waar de tekst wordt bijgeschreven.
 *
 * Het tekstvak groeit met zijn inhoud en heeft dus geen eigen schuifbalk; wat
 * hier schuift is het venster. Dat stopt zodra de lezer zelf omhoog gaat, want
 * wie terugleest wordt anders bij elke tik naar beneden gesleurd.
 */
function useVolgtDeTekst(tekst: string, actief: boolean) {
  useEffect(() => {
    if (!actief) return;
    const el = document.scrollingElement;
    if (!el) return;
    const leestMee = el.scrollHeight - el.scrollTop - el.clientHeight < BIJ_HET_EIND;
    if (leestMee) el.scrollTo({ top: el.scrollHeight });
  }, [tekst, actief]);
}

/**
 * Scherm 1: de eerste poort. Er gebeurt niets met dit gesprek tot de buddy hier
 * op akkoord klikt.
 *
 * Wat hij controleert is de samenvatting, niet het ruwe transcript. Op een
 * gesprek van 28 minuten scheelt dat 24.000 tekens spreektaal tegenover 4.500
 * die je ook werkelijk naleest, en een poort die niemand serieus doorloopt is
 * geen poort.
 *
 * Het is één vrij tekstvak en geen lijst met voorstellen om af te vinken. De
 * buddy weet wat er is gezegd; hij hoeft niet te reageren op wat een model
 * denkt, hij mag het gewoon opschrijven. Het ruwe transcript blijft op de server
 * staan om op terug te vallen.
 *
 * Ook tijdens het samenvatten is dit hetzelfde scherm met hetzelfde tekstvak:
 * de tekst stroomt binnen in het vak waarin hij straks te bewerken is, zonder
 * overgang. Vóór die stap is er geen vak. Omzetten en uitschrijven leveren niets
 * tussentijds op, en een leeg vak leest als iets dat had moeten vullen.
 */
export function TranscriptReview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: gesprek } = useGesprek(id);
  const status = gesprek?.meeting.status;
  const bezig = status === 'TRANSCRIBING' || status === 'UPLOADED';
  const { data, isLoading } = useTranscript(id, status);
  const keurGoed = useKeurTranscriptGoed(id);
  const bewerk = useBewerkTranscript(id);
  const opnieuw = useHersamenvat(id);

  // Ook tijdens het hersamenvatten stroomt er tekst binnen: dat duurt even zo
  // lang en schrijft zijn tussenstand naar dezelfde plek, dus hetzelfde pollen
  // laat hem in hetzelfde vak groeien.
  const streamt = bezig || opnieuw.isPending;
  const { data: voortgang } = useVoortgang(id, streamt);

  const [tekst, setTekst] = useState('');
  const [fout, setFout] = useState('');
  const geladen = useRef<string | null>(null);

  const samenvatting = data?.transcript.samenvatting ?? '';

  // Welke stap er loopt. Nog geen melding binnen betekent dat de verwerking net
  // is gestart en aan het omzetten is; dat is de eerste stap die de server doet.
  // Bij opnieuw samenvatten slaan we de eerdere stappen over.
  const fase: Verwerkingsfase = opnieuw.isPending
    ? 'samenvatten'
    : (voortgang?.voortgang?.fase ?? 'omzetten');
  const secondenInStap = useVerstrekenTijd(voortgang?.voortgang?.sinds);

  // Alleen de samenvatting streamt; het transcript komt in één keer binnen. Bij
  // de eerdere stappen is de oplopende tijd dus het enige bewijs dat er iets
  // gebeurt, en is er geen tekstvak.
  const stroomt = streamt && fase === 'samenvatten';

  const stroom = useGeleidelijk(useLaatsteStand(voortgang?.voortgang?.tekst ?? null, stroomt));
  useVolgtDeTekst(stroom, stroomt);

  // Alleen bij een nieuwe of opnieuw gemaakte samenvatting overschrijven, anders
  // gooit een achtergrondverversing weg waar de buddy net aan zat te typen.
  useEffect(() => {
    if (samenvatting && geladen.current !== samenvatting) {
      geladen.current = samenvatting;
      setTekst(samenvatting);
    }
  }, [samenvatting]);

  if (status === 'FAILED') {
    return (
      <Alert variant="destructive">
        <AlertTitle>{leesbareFout(gesprek?.meeting.failureReason)}</AlertTitle>
      </Alert>
    );
  }
  // Op de status en niet op "is er al een transcript": de transcriptrij bestaat
  // al zodra de tekst is opgeslagen, terwijl daarna nog een halve minuut wordt
  // samengevat. Met de oude conditie viel het scherm in die tijd door naar een
  // leeg bewerkveld met de melding dat er geen samenvatting was.
  if (!bezig && isLoading) return <Skeleton className="h-96 w-full" />;
  if (!bezig && !data) {
    return (
      <Alert>
        <AlertTitle>Er is nog geen transcript voor dit gesprek.</AlertTitle>
      </Alert>
    );
  }

  const alGoedgekeurd = Boolean(data?.transcript.confirmedAt);
  const gewijzigd = tekst !== samenvatting;
  const bewaart = bewerk.isPending || keurGoed.isPending;

  async function bewaarEnGaVerder() {
    setFout('');
    try {
      if (gewijzigd) await bewerk.mutateAsync(tekst);
      await keurGoed.mutateAsync();
      navigate(`/gesprekken/${id}/hulpvragen`);
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  // Hetzelfde vak tijdens en na het samenvatten, alleen niet te bewerken zolang
  // er tekst binnenkomt. `readOnly` en niet `disabled`: dat laatste dempt het
  // vak, en dan is er toch een overgang te zien zodra de verwerking klaar is.
  // `field-sizing-content` laat het meegroeien met zijn inhoud: een vak met een
  // eigen schuifbalk maakt van lezen bladeren.
  const tekstvak = (
    <Textarea
      aria-label="Samenvatting van dit gesprek"
      className="min-h-72"
      disabled={alGoedgekeurd}
      onChange={(e) => setTekst(e.target.value)}
      placeholder={stroomt ? '' : 'Typ hier de samenvatting van dit gesprek.'}
      readOnly={stroomt}
      spellCheck
      value={stroomt ? stroom : tekst}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {alGoedgekeurd && (
        <Alert>
          <CheckIcon />
          <AlertTitle>Je hebt deze samenvatting goedgekeurd.</AlertTitle>
        </Alert>
      )}

      {!streamt && !samenvatting && !alGoedgekeurd && (
        <Alert>
          <AlertTitle>
            Er is nog geen samenvatting gemaakt. Klik op “Opnieuw samenvatten”, of typ hem zelf.
          </AlertTitle>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{streamt ? 'Bezig met dit gesprek' : 'Samenvatting'}</CardTitle>

          {streamt ? (
            // Lucht tussen de titel en de balk: die twee plakten aan elkaar.
            <Verwerkingsspoor
              actief={fase}
              bijschrift={tijdTekst(secondenInStap)}
              className="mt-3"
              secondenInStap={secondenInStap}
            />
          ) : (
            !alGoedgekeurd && (
              // Expliciet, want een tekstvak dat al vol staat nodigt niet uit om
              // er iets bij te schrijven, en juist dat is hier de bedoeling.
              <CardDescription>
                Klopt de samenvatting of was er meer? Pas de tekst aan of typ er zelf bij.
              </CardDescription>
            )
          )}

          {/* Alleen als er niets loopt. Tijdens het verwerken draait het rondje
              al in de stappenbalk; een tweede hier las als een tweede proces. */}
          {!streamt && (
            <CardAction className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground tabular-nums">
                {tekst.length.toLocaleString('nl-NL')} tekens
              </span>
              {!alGoedgekeurd && (
                <Button
                  onClick={async () => {
                    setFout('');
                    try {
                      const uit = await opnieuw.mutateAsync();
                      geladen.current = uit.samenvatting;
                      setTekst(uit.samenvatting);
                    } catch (err) {
                      setFout(err instanceof ApiError ? err.message : String(err));
                    }
                  }}
                  size="sm"
                  variant="ghost"
                >
                  <RefreshCwIcon data-icon="inline-start" />
                  Opnieuw samenvatten
                </Button>
              )}
            </CardAction>
          )}
        </CardHeader>

        {!streamt && <CardContent>{tekstvak}</CardContent>}

        {!streamt && !alGoedgekeurd && (
          <CardFooter className="flex-wrap justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Hierna gaan we verder met dit gesprek. De opname blijft bewaard tot je dat hebt
              afgerond.
            </p>
            <Button disabled={!tekst.trim() || bewaart} onClick={bewaarEnGaVerder}>
              {bewaart && <Spinner data-icon="inline-start" />}
              {bewaart ? 'Bezig…' : 'Goedkeuren en verder'}
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Een eigen kaart, los van de balk erboven: die twee gaan over
          verschillende dingen, en de balk blijft zo door alle stappen heen
          hetzelfde. */}
      {stroomt && (
        <Card>
          <CardContent>{tekstvak}</CardContent>
        </Card>
      )}

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}
    </div>
  );
}
